import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { BlogPostShell, POSTS } from "@/pages/Blog";

const POST = POSTS.find((p) => p.slug === "season-league-tables-and-photographer-of-the-year");
const OG = "https://cameraclubscore.com/og/blog-generic.png";
const URL = "https://cameraclubscore.com/blog/season-league-tables-and-photographer-of-the-year";

export default function PostLeague() {
  return (
    <>
      <Helmet>
        <title>{`${POST.title} — Camera Club Score`}</title>
        <meta name="description" content={POST.excerpt} />
        <link rel="canonical" href={URL} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={URL} />
        <meta property="og:title" content={POST.title} />
        <meta property="og:description" content={POST.excerpt} />
        <meta property="og:image" content={OG} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={OG} />
      </Helmet>
      <BlogPostShell post={POST}>
        <p className="lead">
          The end-of-season awards evening is your club's biggest emotional
          moment. Fair, defensible league tables are the foundation. Here's
          how to design a scoring system that produces exciting finishes
          nobody can argue with.
        </p>

        <h2>How do I calculate league tables automatically?</h2>
        <p>
          A league table sums each photographer's scores across the season,
          applies your scoring rule (sum of all, best-N, or PoY points),
          and ranks the totals. Purpose-built tools like Camera Club Score
          do this live — you'll never open Excel again.
        </p>

        <h2>How do I keep season totals?</h2>
        <p>
          Every score entered during a score entry session automatically
          contributes to the season total in Camera Club Score. Competitions
          flagged <strong>Not counted</strong> (practice rounds, exhibition
          matches) are excluded — this matches most clubs' handbooks.
        </p>

        <h2>How do I calculate averages?</h2>
        <p>
          Average score per entry, per photographer, per season. Useful
          for members who miss competitions — their total looks bad, but
          their average is competitive. Camera Club Score shows both
          on the Season Stats page.
        </p>

        <h2>Can software calculate competition winners?</h2>
        <p>
          Yes — instantly. The moment the last score is entered, the
          Results page shows the winner, placements, and any ties.
          There's no "wait for the treasurer to do the maths" phase.
        </p>

        <h2>How do I record Photographer of the Year?</h2>
        <p>
          Two common approaches:
        </p>
        <ul>
          <li><strong>Sum of all scores</strong> — rewards consistency and volume. Members who enter every round win.</li>
          <li><strong>Best-N scores</strong> — rewards peak performance. Members who enter fewer times but do brilliantly can still win.</li>
        </ul>
        <p>
          Best-N (typically best 5 of 8) is fairer for members who miss
          competitions for genuine reasons. Camera Club Score lets you
          configure this per-club under Settings → Scoring rules.
        </p>

        <h2>Can I compare previous seasons?</h2>
        <p>
          Seasons in Camera Club Score never delete. Open any past season
          from the season selector at the top of Season Stats — you'll
          see the same league table, exports, and totals as you saw the
          day it ended.
        </p>

        <h2>What about tie-breakers?</h2>
        <p>
          Common tie-break rules, in order of popularity:
        </p>
        <ol>
          <li><strong>Both share the position.</strong> Simple, generous, popular.</li>
          <li><strong>Most 1st placements.</strong> Rewards peak performance among tied totals.</li>
          <li><strong>Head-to-head best competition.</strong> Complex, but produces a clean winner.</li>
        </ol>

        <h2>What about certificates?</h2>
        <p>
          At season end, Camera Club Score's <strong>Certificates CSV</strong>{" "}
          export gives you a ready-to-mail-merge list of every image that
          hit the top mark. One CSV, one mail-merge, all your certificates
          printed in an evening.
        </p>

        <p>
          It's all built in. <Link to="/register">Try Camera Club Score
          free</Link>.
        </p>
      </BlogPostShell>
    </>
  );
}
